import React, { useEffect, useLayoutEffect, useState } from 'react';
import HeroSection from './components/HeroSection';
import PersonalisedSkeleton from './components/PersonalisedSkeleton';
import BodyFrameComponent from './components/BodyFrameComponent';
import NotesFromCoach from './components/NotesFromCoach';
import LifeStyleAndGoals from './components/LifeStyleAndGoals';
import PageHeader from './components/PageHeader';
import GoalsBanner from './components/GoalsBanner';
import PdfLogo from './components/PdfLogo';
import BodyAreaDetailsSection from './components/BodyAreaDetailsSection';
import GetInTouch from './components/GetInTouch';
// @ts-ignore
import { get_api } from '../../../../redux/api_funcs';
// @ts-ignore
import { get_api_url } from '../../../../utils/urls';
import { ThreeMonthPlanItem } from '../PersonalisedPlan/service/threeMonthPlanService';
import PlansTable from './components/PlansTable';

export interface CoachInfo {
  name: string;
  avatar_url?: string;
  more_experts: string[];
}

interface NoteItem {
  note: string;
  note_type: 'teacher' | 'ai';
  selected: boolean;
}

interface ThreeMonthsData {
  sent_to_student: boolean;
  evaluated_on: string;
  teacher_name: string;
  teacher_uuid: string;
  three_months_plan: ThreeMonthPlanItem[];
  student: {
    first_name: string;
    gender: string;
    avatar_url?: string;
    currency?: string;
    currency_symbol?: string;
  };
  coach_info: CoachInfo;
  notes?: NoteItem[];
  lifestyle?: string[] | null;
  goals?: string[] | null;
}

interface PersonalisedData {
  concierge_info?: Record<string, any>;
  recommended_classes?: string[];
  plans?: any[];
  sent_to_student: boolean;
  student?: {
    first_name: string;
    gender?: string;
    avatar_url?: string;
    currency?: string;
    currency_symbol?: string;
  };
}

const resolveNote = (notes?: NoteItem[]): string => {
  if (!notes || notes.length === 0) return '';
  const teacher = notes.find((n) => n.note_type === 'teacher');
  const ai = notes.find((n) => n.note_type === 'ai');
  return teacher?.note ? teacher.note : ai?.note || '';
};

const PersonalizedPlanPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [personalisedData, setPersonalisedData] = useState<PersonalisedData | null>(null);
  const [threeMonthsData, setThreeMonthsData] = useState<ThreeMonthsData | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const studentUuid = urlParams.get('student_uuid') ?? '';
  const isPdfMode = urlParams.get('pdf') === '1';

  // The PDF export must always render in the LIGHT theme, whatever theme the user
  // is currently in. Dark mode is a `dark` class on <html> (see useTheme.js /
  // the anti-flash bootstrap in index.html, which adds it from the shared
  // localStorage 'user_theme' key — inherited here because the export iframe is
  // same-origin). `pdf=1` is effectively "print mode", so strip that class to
  // pin the page to light before html2canvas rasterises it. useLayoutEffect runs
  // before paint, so the page is never captured in dark. No cleanup needed: the
  // export iframe is discarded right after capture.
  useLayoutEffect(() => {
    if (isPdfMode) {
      document.documentElement.classList.remove('dark');
    }
  }, [isPdfMode]);

  useEffect(() => {
    if (!studentUuid) return;

    // Personalised pricing API
    const pricingUrl = get_api_url(`/v2/cms_user/personalised_plan/${studentUuid}`);
    const pricingCall = get_api(pricingUrl, true).then((result: any) => {
      const data = result.data?.data;
      setPersonalisedData(data);
    });

    // Three months plan API
    const threeMonthsUrl = get_api_url(
      `/v2/cms_user/personalised_plan/three_months_plan/${studentUuid}`
    );
    const threeMonthsCall = get_api(threeMonthsUrl, true).then((result: any) => {
      const data = result.data?.data;
      setThreeMonthsData(data ?? null);
    });

    Promise.all([pricingCall, threeMonthsCall]).finally(() => setLoading(false));
  }, [studentUuid]);

  if (loading) {
    return <PersonalisedSkeleton />;
  }

  const nothingToShow = !threeMonthsData?.sent_to_student && !personalisedData?.sent_to_student;

  if (nothingToShow) {
    return (
      <>
        {!isPdfMode && <PageHeader />}
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-[25px] text-center">
          <h2 className="text-[22px] font-semibold leading-[28px] text-[#3C0074] mb-[12px]">
            Personalised Plan Not Available
          </h2>
          <p className="text-[15px] font-[450] leading-[22px] text-[#8F8F8F] max-w-[420px]">
            The personalised pricing plan and assessment for this student haven't been shared yet.
            Once the teacher or concierge team shares the plan, it will appear here.
          </p>
        </div>
      </>
    );
  }

  const showThreeMonthsSection = Boolean(threeMonthsData?.sent_to_student);
  const showPlansTable = Boolean(personalisedData?.sent_to_student);
  const resolvedNote = resolveNote(threeMonthsData?.notes);

  return (
    <section id="personalised-pricing">
      {!isPdfMode && <PageHeader />}

      {showThreeMonthsSection && (
        <>
          {isPdfMode ? (
            <PdfLogo />
          ) : (
            <GoalsBanner
              teacherName={threeMonthsData!.teacher_name}
              evaluatedOn={threeMonthsData!.evaluated_on}
            />
          )}
          <div
            className={`flex items-center flex-col justify-center ${isPdfMode ? 'bg-white pt-[20px]' : 'bg-[#FBFBFB] pt-[64px] md:pt-[76px]'}`}
          >
            <div
              className={`w-full flex justify-center ${isPdfMode ? '' : 'border-b border-[#F0F0F0]'}`}
            >
              <div className="px-[25px] lg:px-0 w-full max-w-[940px] zm:mb-[25px] md:mb-[50px]">
                <HeroSection studentDetails={threeMonthsData!.student} isPdfMode={isPdfMode} />
                {threeMonthsData!.three_months_plan.length > 0 && (
                  <BodyFrameComponent
                    gender={threeMonthsData!.student.gender}
                    planItems={threeMonthsData!.three_months_plan}
                    isPdfMode={isPdfMode}
                  />
                )}
                <div
                  className={`mt-[40px] ${(threeMonthsData!.lifestyle?.length ?? 0) > 0 || (threeMonthsData!.goals?.length ?? 0) > 0 ? 'mb-[30px] md:mb-[40px]' : ''}`}
                >
                  <NotesFromCoach
                    coachesAndNotes={{
                      coachInfo: threeMonthsData!.coach_info,
                      note: resolvedNote
                    }}
                    teacherUuid={threeMonthsData!.teacher_uuid}
                    isPdfMode={isPdfMode}
                  />
                </div>
                <LifeStyleAndGoals
                  LifeStyleAndGoalsData={{
                    lifeStyle: threeMonthsData!.lifestyle ?? [],
                    goals: threeMonthsData!.goals ?? []
                  }}
                />
                {isPdfMode && threeMonthsData!.three_months_plan.length > 0 && (
                  <BodyAreaDetailsSection
                    gender={threeMonthsData!.student.gender}
                    planItems={threeMonthsData!.three_months_plan}
                  />
                )}
                {isPdfMode && <GetInTouch />}
              </div>
            </div>
          </div>
        </>
      )}

      {showPlansTable && !isPdfMode && (
        <div className="w-full flex items-center justify-center bg-white dark:bg-gray-800 pb-[80px] md:pb-[104px] lg:px-[25px]">
          <PlansTable
            conciergeData={personalisedData!.concierge_info as any}
            plan={personalisedData!.plans as any}
            recomendedGroupClasses={personalisedData!.recommended_classes as any}
            studentFirstName={personalisedData!.student?.first_name ?? ''}
          />
        </div>
      )}
    </section>
  );
};

export default PersonalizedPlanPage;
